import {
  getAuth,
  updatePassword,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  deleteUser
} from 'firebase/auth';
import { doc, updateDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Active l'accès authentification pour un bénéficiaire
 * La création du compte Firebase est gérée automatiquement par la Cloud Function trigger createClientAuthAccount
 * Cette fonction met simplement à jour Firestore pour déclencher le trigger
 * @param {object} db - Instance Firestore
 * @param {string} clientId - ID du client
 * @param {string} email - Email du client (non utilisé, juste pour compatibilité)
 * @param {string} password - Mot de passe du client (non utilisé, généré automatiquement)
 * @param {object} clientData - Données du client
 */
export const createClientAuthAccount = async (db, clientId, email, password, clientData) => {
  try {
    const activateFunction = httpsCallable(functions, 'activateBeneficiary');

    const result = await activateFunction({
      clientId: clientId,
      clientData: {
        firstName: clientData.firstName,
        lastName: clientData.lastName,
        email: clientData.email
      }
    });

    if (result.data && result.data.success) {
      return {
        success: true,
        email: result.data.email,
        password: result.data.password,
        uid: result.data.uid,
        message: 'Accès activé avec succès'
      };
    } else {
      throw new Error(result.data?.error || 'Erreur inconnue lors de l\'activation');
    }
  } catch (error) {
    console.error('Erreur lors de l\'activation de l\'accès client:', error);
    return { success: false, error: error.message };
  }
};


/**
 * Désactive l'accès authentification d'un client et supprime le compte Firebase
 * @param {object} db - Instance Firestore
 * @param {string} clientId - ID du client
 */
export const disableClientAuth = async (db, clientId) => {
  try {
    // Récupérer l'authUid nécessaire pour la désactivation
    const clientDoc = await getDoc(doc(db, 'clients', clientId));
    if (!clientDoc.exists()) {
      return { success: false, error: 'Client non trouvé' };
    }
    const clientData = clientDoc.data();

    if (!clientData.authUid) {
      // Si pas d'UID, désactiver juste le flag Firestore
      await updateDoc(doc(db, 'clients', clientId), {
        hasAuthAccess: false,
        authPassword: null
      });
      return { success: true };
    }

    const disableFunction = httpsCallable(functions, 'disableBeneficiary');
    const result = await disableFunction({
      clientId: clientId,
      authUid: clientData.authUid
    });

    if (result.data && result.data.success) {
      return { success: true };
    } else {
      throw new Error(result.data?.error || 'Erreur lors de la désactivation');
    }
  } catch (error) {
    console.error('Erreur lors de la désactivation de l\'accès client:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Vérifie si un client a accès à l'authentification
 * @param {object} db - Instance Firestore
 * @param {string} clientId - ID du client
 */
export const checkClientAuthAccess = async (db, clientId) => {
  try {
    const clientDoc = await getDoc(doc(db, 'clients', clientId));
    if (clientDoc.exists()) {
      const clientData = clientDoc.data();
      return {
        hasAccess: clientData.hasAuthAccess || false,
        authEmail: clientData.authEmail || null,
        authPassword: clientData.authPassword || null
      };
    }
    return { hasAccess: false, authEmail: null, authPassword: null };
  } catch (error) {
    console.error('Erreur lors de la vérification de l\'accès client:', error);
    return { hasAccess: false, authEmail: null, authPassword: null };
  }
};

/**
 * Normalise une chaîne de caractères (supprime accents, tirets, apostrophes, espaces)
 */
const normalizeString = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprimer accents
    .replace(/[-'\s]/g, '')           // Supprimer tirets, apostrophes, espaces
    .toLowerCase();
};

/**
 * Génère l'email du client pour la connexion
 * Si le client a déjà un email, l'utiliser
 * Sinon, générer prenom.nom@agentime.fr
 */
const generateClientEmail = (firstName, lastName, existingEmail) => {
  // Si le client a déjà un email renseigné dans sa fiche, on l'utilise
  if (existingEmail && existingEmail.trim() !== '') {
    return existingEmail.trim();
  }

  // Sinon, générer client.prenom.nom@agentime.fr
  const normalizedFirst = normalizeString(firstName);
  const normalizedLast = normalizeString(lastName);
  return `client.${normalizedFirst}.${normalizedLast}@agentime.fr`;
};

/**
 * Génère un mot de passe simplifié basé sur le nom
 * Format: PrenomN2025-Xx9 (3 caractères aléatoires après le tiret)
 */
const generateSimplePassword = (firstName, lastName) => {
  const normalizedFirst = normalizeString(firstName);
  const normalizedLast = normalizeString(lastName);
  const year = new Date().getFullYear();

  // Première lettre en majuscule pour le prénom
  const firstNameCapitalized = normalizedFirst.charAt(0).toUpperCase() + normalizedFirst.slice(1);
  const lastInitial = normalizedLast.charAt(0).toUpperCase();

  // 3 caractères aléatoires (lettres + chiffres)
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let randomPart = '';
  for (let i = 0; i < 3; i++) {
    randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return `${firstNameCapitalized}${lastInitial}${year}-${randomPart}`;
};

/**
 * Génère un mot de passe aléatoire pour un client
 * @param {number} length - Longueur du mot de passe (paramètre conservé pour compatibilité)
 */
export const generateRandomPassword = (length = 12) => {
  // Utilise le nouveau format de mot de passe simplifié
  // Pour compatibilité, on génère un mot de passe avec des valeurs par défaut
  const chars = '0123456789';
  let randomPart = '';
  for (let i = 0; i < 4; i++) {
    randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  const year = new Date().getFullYear();
  return `User${year}!${randomPart}`;
};

/**
 * Récupère tous les clients avec accès authentification
 * @param {object} db - Instance Firestore
 */
export const getClientsWithAuthAccess = async (db) => {
  try {
    const clientsRef = collection(db, 'clients');
    const q = query(clientsRef, where('hasAuthAccess', '==', true));
    const querySnapshot = await getDocs(q);

    const clients = [];
    querySnapshot.forEach((doc) => {
      clients.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return clients;
  } catch (error) {
    console.error('Erreur lors de la récupération des clients avec accès:', error);
    return [];
  }
};

/**
 * Réinitialise le mot de passe d'un client
 * @param {object} db - Instance Firestore
 * @param {string} clientId - ID du client
 */
export const resetClientPassword = async (db, clientId) => {
  try {
    // Récupérer l'authUid et le prénom pour la réinitialisation
    const clientDoc = await getDoc(doc(db, 'clients', clientId));
    if (!clientDoc.exists()) {
      return { success: false, error: 'Client non trouvé' };
    }
    const clientData = clientDoc.data();

    if (!clientData.authUid) {
      return { success: false, error: 'Compte non activé' };
    }

    const resetFunction = httpsCallable(functions, 'resetBeneficiaryPassword');
    const result = await resetFunction({
      clientId: clientId,
      authUid: clientData.authUid,
      firstName: clientData.firstName
    });

    if (result.data && result.data.success) {
      return { success: true, newPassword: result.data.newPassword };
    } else {
      throw new Error(result.data?.error || 'Erreur lors de la réinitialisation');
    }
  } catch (error) {
    console.error('Erreur lors de la réinitialisation du mot de passe:', error);
    return { success: false, error: error.message };
  }
};
